import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { getEpsColor, getScoreBand } from '@/lib/scoring';

interface TestSession {
  id: string;
  date: string;
  eps: number;
  engineScore: number;
  ignitionScore: number;
  bounceScore: number;
}

interface ProgressHeroProps {
  deltas: { eps: number; engine: number; ignition: number; bounce: number };
  first: TestSession;
  latest: TestSession;
}

function DeltaCard({
  label, delta, current, color, isHero = false,
}: {
  label: string; delta: number; current: number; color: string; isHero?: boolean;
}) {
  const band = getScoreBand(current);
  return (
    <div
      className={`rounded-xl border p-4 ${isHero ? 'col-span-2' : ''}`}
      style={{ backgroundColor: `${color}08`, borderColor: `${color}25` }}
    >
      <p className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
      <div className="flex items-end gap-3 mb-2">
        <span className={`font-mono tabular-nums font-bold ${isHero ? 'text-5xl' : 'text-3xl'}`} style={{ color }}>
          {current}
        </span>
        <div className="mb-1">
          <div className={`flex items-center gap-1 font-mono font-semibold ${isHero ? 'text-base' : 'text-sm'} ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
            {delta > 0 ? <TrendingUp size={14} /> : delta < 0 ? <TrendingDown size={14} /> : <Minus size={14} />}
            {delta > 0 ? '+' : ''}{delta}
          </div>
          <p className="text-xs text-zinc-600">from baseline</p>
        </div>
      </div>
      <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${current}%`, backgroundColor: color }} />
      </div>
      {isHero && (
        <p className="text-xs mt-2" style={{ color: band.color }}>{band.label}</p>
      )}
    </div>
  );
}

export default function ProgressHero({ deltas, first, latest }: ProgressHeroProps) {
  const epsColor = getEpsColor(latest.eps);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <DeltaCard label="Explosive Power Score" delta={deltas.eps} current={latest.eps} color={epsColor} isHero />
      <DeltaCard label="Engine" delta={deltas.engine} current={latest.engineScore} color="#EF4444" />
      <DeltaCard label="Ignition" delta={deltas.ignition} current={latest.ignitionScore} color="#F59E0B" />
      <DeltaCard label="Bounce" delta={deltas.bounce} current={latest.bounceScore} color="#10B981" />
    </div>
  );
}